import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  cancelPlacement,
  setActiveShapeTool,
  selectShape,
  removeShape,
  setPanelCollapsed,
  undo,
  redo,
} from '../../store/slices/missionPlannerSlice';
import { PlannerMap } from './PlannerMap';
import { PlannerPanel } from './PlannerPanel';

const isTextEntry = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null;
  const tag = node?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || node?.isContentEditable === true;
};

/**
 * Mission Planner view (issue #293). Controls + lists dock on the left in a
 * collapsible panel (issue #343); the map fills the remaining space for
 * click-to-place waypoints and coverage shapes.
 *
 * Keyboard: Escape cancels the current mode/selection; Delete removes the
 * selected shape; Ctrl/Cmd+Z undoes and Ctrl/Cmd+Shift+Z (or Ctrl+Y) redoes
 * the last edit (all suppressed while a text field is focused).
 */
export const MissionPlanner: React.FC = () => {
  const dispatch = useDispatch();
  const placementMode = useSelector(
    (state: RootState) => state.missionPlanner.placementMode
  );
  const activeShapeTool = useSelector(
    (state: RootState) => state.missionPlanner.activeShapeTool
  );
  const selectedShapeId = useSelector(
    (state: RootState) => state.missionPlanner.selectedShapeId
  );
  const panelCollapsed = useSelector(
    (state: RootState) => state.missionPlanner.panelCollapsed
  );

  // Below the panel's own breakpoint (md), stack the map over the panel so the
  // planner is usable on phones/tablets (320–768px) without horizontal scroll
  // (issue #325). At md+ the panel sits beside the map as a fixed sidebar.
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (placementMode !== 'idle') {
          dispatch(cancelPlacement());
        } else if (activeShapeTool) {
          dispatch(setActiveShapeTool(null));
        } else if (selectedShapeId) {
          dispatch(selectShape(null));
        }
        return;
      }
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedShapeId &&
        !isTextEntry(e.target)
      ) {
        e.preventDefault();
        dispatch(removeShape(selectedShapeId));
        return;
      }
      // Undo / redo — let native undo win inside text fields.
      if ((e.ctrlKey || e.metaKey) && !isTextEntry(e.target)) {
        const key = e.key.toLowerCase();
        if (key === 'z' && !e.shiftKey) {
          e.preventDefault();
          dispatch(undo());
        } else if ((key === 'z' && e.shiftKey) || key === 'y') {
          e.preventDefault();
          dispatch(redo());
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [placementMode, activeShapeTool, selectedShapeId, dispatch]);

  return (
    <Box
      data-layout={stacked ? 'stacked' : 'columns'}
      sx={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        flexGrow: 1,
        overflow: 'hidden',
      }}
    >
      {/* Left-docked panel; hidden when collapsed to give the map more room. */}
      {!panelCollapsed && <PlannerPanel />}
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          // Stacked, the map needs an explicit height (flex-basis in a column
          // collapses otherwise); side-by-side it fills the row.
          minHeight: stacked ? 320 : 'auto',
        }}
      >
        {/* Visible affordance to bring the panel back once collapsed. */}
        {panelCollapsed && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ChevronRight />}
            onClick={() => dispatch(setPanelCollapsed(false))}
            sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1200 }}
          >
            Planner
          </Button>
        )}
        <PlannerMap />
      </Box>
    </Box>
  );
};
